import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { MARKETING_EMAIL_CONSENT_VERSION } from '../../constants/legal';
import { SHEET_CLOSE_ARIA_LABEL } from '../../constants/sheetCopy';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  answerEmailMarketingPrompt,
  errorDetailMessage,
  httpStatusFromError,
} from '../../services/apiService';
import { FILLS, FONTS, RADII, SIZING, TYPE } from '../../styles/tokens';
import { Sheet, SheetTitle } from '../common/Sheet';
import MarketingEmailConsent from '../forms/MarketingEmailConsent';
import { CloseIcon } from '../icons';

const PROMPT_TITLE = 'Belhop offers by email';
const FALLBACK_ERROR = 'Could not save your choice. Please try again.';
const CHECKBOX_ID = 'marketing-email-prompt-consent';
const ALREADY_ANSWERED_STATUS = 409;
const SUPPRESSED_PATH_PREFIXES = ['/admin', '/checkout', '/auth/callback'];

/**
 * One-time offer of marketing email for a user whose profile is flagged pending (a new Google
 * sign-up). Any answer, including dismissal, is recorded; dismissing counts as declining.
 */
const MarketingEmailPrompt: React.FC = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, user, setUserData, retryProfileFetch } = useAuth();
  const isMobile = useIsMobile();
  const [checked, setChecked] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isHidden, setIsHidden] = useState(false);
  const userId = user?.id;

  // A failed dismissal hides the prompt for this page session only for the user who dismissed it.
  useEffect(() => {
    setIsHidden(false);
  }, [userId]);

  const isSuppressedPath = SUPPRESSED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  if (
    !isAuthenticated ||
    user?.email_marketing_prompt_pending !== true ||
    isSuppressedPath ||
    isHidden
  ) {
    return null;
  }

  const submit = async (optedIn: boolean, isDismissal: boolean) => {
    if (isSending) return;
    setIsSending(true);
    setError(null);
    try {
      const updated = await answerEmailMarketingPrompt({
        opted_in: optedIn,
        disclosure_version: MARKETING_EMAIL_CONSENT_VERSION,
      });
      setUserData(updated);
      setIsHidden(true);
    } catch (err: unknown) {
      if (httpStatusFromError(err) === ALREADY_ANSWERED_STATUS) {
        await retryProfileFetch();
        setIsHidden(true);
      } else if (isDismissal) {
        setIsHidden(true);
      } else {
        setError(errorDetailMessage(err) || FALLBACK_ERROR);
      }
    } finally {
      setIsSending(false);
    }
  };

  const dismiss = () => {
    void submit(false, true);
  };

  return (
    <Sheet
      layout={isMobile ? 'bottom' : 'centered'}
      onClose={dismiss}
      padding="20px"
      backdropTestId="marketing-email-prompt-backdrop"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <SheetTitle style={{ ...TYPE.sheetTitle, color: FILLS.textPrimary, margin: 0 }}>
          {PROMPT_TITLE}
        </SheetTitle>
        <button
          type="button"
          aria-label={SHEET_CLOSE_ARIA_LABEL}
          onClick={dismiss}
          disabled={isSending}
          style={{
            minWidth: '44px',
            minHeight: '44px',
            background: 'none',
            border: 'none',
            color: FILLS.textPrimary,
            cursor: isSending ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CloseIcon />
        </button>
      </div>
      <MarketingEmailConsent
        id={CHECKBOX_ID}
        checked={checked}
        onChange={setChecked}
        disabled={isSending}
        busy={isSending}
      />
      {error !== null && (
        <p
          role="alert"
          style={{
            margin: '12px 0 0',
            fontFamily: FONTS.body,
            fontSize: '13px',
            color: FILLS.errorText,
          }}
        >
          {error}
        </p>
      )}
      <button
        type="button"
        onClick={() => void submit(checked, false)}
        disabled={isSending}
        style={{
          marginTop: '16px',
          width: '100%',
          height: SIZING.controlHeight,
          background: FILLS.gold,
          border: 'none',
          borderRadius: RADII.control,
          color: FILLS.page,
          fontFamily: FONTS.body,
          fontSize: '14px',
          fontWeight: 600,
          cursor: isSending ? 'not-allowed' : 'pointer',
          opacity: isSending ? 0.6 : 1,
        }}
      >
        Continue
      </button>
    </Sheet>
  );
};

export default MarketingEmailPrompt;
